import {
  BarChart3,
  CircleHelp,
  CornerDownRight,
  Eye,
  ListChecks,
  MessageCircleQuestion,
  MousePointer2,
  Split,
  SquareArrowOutUpRight,
  Swords,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react';
import type { ReactNode } from 'react';

import type {
  Persona,
  ResearchRoomMessage,
  ResearchRoomMilestone,
} from '@repo/contracts';
import { personaChatModelLabel } from '@repo/contracts/persona-models';
import { Tooltip } from '@repo/ui/tooltip';

import { useIsAdmin } from '../admin/admin-route';
import { accentOf } from '../persona/persona-accent';
import { PersonaAvatar } from '../persona/persona-avatar';
import { segmentMentions } from './mentions';

/**
 * Floto's mark. The 32px file for a small avatar (crisp at 2x), the 128px one
 * wherever it is drawn large — `srcSet` lets the browser pick.
 */
export function FlotoMark({ size = 'sm' }: { size?: 'lg' | 'sm' }) {
  return (
    <img
      alt=""
      aria-hidden="true"
      className={size === 'lg' ? 'size-12' : 'size-5'}
      src={size === 'lg' ? '/icons/icon-128.png' : '/icons/icon-32.png'}
      srcSet={
        size === 'lg'
          ? undefined
          : '/icons/icon-32.png 1x, /icons/icon-128.png 4x'
      }
    />
  );
}

/**
 * "Where": the part of the screen a message is about. Pressing it takes the
 * canvas there again — the spotlight the message raised when it arrived, on
 * demand.
 */
export function FocusChip({
  active = false,
  label,
  onClick,
  tone = 'light',
}: {
  active?: boolean;
  label: string;
  onClick: () => void;
  tone?: 'dark' | 'light';
}) {
  return (
    <Tooltip label="Show on the canvas">
      <button
        className={[
          'inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-pill px-3 py-1.5 text-caption font-semibold transition active:scale-[0.97]',
          tone === 'dark'
            ? 'bg-white/10 text-white/80 hover:bg-white/20 hover:text-white'
            : active
              ? 'bg-where-soft text-where ring-1 ring-where/40'
              : 'bg-where-soft text-where hover:brightness-95',
        ].join(' ')}
        onClick={onClick}
        type="button"
      >
        <MousePointer2 aria-hidden="true" className="size-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </button>
    </Tooltip>
  );
}

/** A line still being written — the shine the fix list uses for "working". */
export function Working({ children }: { children: ReactNode }) {
  return (
    <span className="animate-text-shimmer bg-gradient-to-r from-text-muted via-text-primary to-text-muted bg-[length:200%_100%] bg-clip-text text-ui text-transparent motion-reduce:animate-none motion-reduce:bg-none motion-reduce:text-text-muted">
      {children}
    </span>
  );
}

function initialsOf(name: string): string {
  const parts = name
    .trim()
    .split(/[\s@._-]+/)
    .filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || 'You';
}

/**
 * The researcher's question — the one dark surface in the room, so every
 * round starts somewhere the eye can find while scrolling back.
 */
export function QuestionBubble({
  asker,
  message,
  messageId,
  personas,
  onSpotlight,
  poll = false,
  sending = false,
}: {
  /** Whoever is signed in, for the initials beside the bubble. */
  asker: string;
  message: Pick<
    ResearchRoomMessage,
    'about' | 'focus' | 'independent' | 'target' | 'targets' | 'text'
  >;
  personas: Map<string, Persona>;
  /** On its way to the server; drawn quieter until it lands. */
  sending?: boolean;
  /** Set for a poll, so the bubble says it is one. */
  poll?: boolean;
  /** Takes the canvas to the part of the screen the question is about. */
  onSpotlight?: () => void;
  /** Its id in the thread, for hovering it to light its place. */
  messageId?: string;
}) {
  const everyone = [...personas.values()];
  const ids = message.targets ?? (message.target ? [message.target] : []);
  const to = ids.flatMap((id) => personas.get(id) ?? []);
  // Who it was put to shows as chips above the question, so the question
  // itself reads without the @-names in it.
  const said = segmentMentions(message.text, everyone)
    .filter((segment) => to.length === 0 || segment.type !== 'mention')
    .map((segment) => segment.text)
    .join('')
    .replace(/\s{2,}/g, ' ')
    .trim();
  const notes = [
    message.about
      ? {
          Icon: message.about.mode === 'why' ? MessageCircleQuestion : Swords,
          text: `${message.about.mode === 'why' ? 'Digging into' : 'Challenging'} ${message.about.speakerName.split(' ')[0] ?? ''}`,
        }
      : null,
    message.independent ? { Icon: Split, text: 'Answering separately' } : null,
    poll ? { Icon: BarChart3, text: 'Poll' } : null,
  ].filter((note) => note !== null);

  return (
    <div
      className={`flex items-end justify-end gap-2.5 pt-3 pl-10 first:pt-0 duration-200 animate-in fade-in slide-in-from-bottom-1 motion-reduce:animate-none ${sending ? 'opacity-60' : ''}`}
      data-message-id={messageId}
    >
      <div className="min-w-0 rounded-card border border-hairline bg-surface px-4 py-3 shadow-soft">
        {notes.length > 0 ? (
          <p className="mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-tag font-semibold text-text-muted">
            {notes.map(({ Icon, text }) => (
              <span className="flex items-center gap-1" key={text}>
                <Icon aria-hidden="true" className="size-3" />
                {text}
              </span>
            ))}
          </p>
        ) : null}
        {!message.about && to.length > 0 ? (
          <p className="mb-2 flex flex-wrap gap-1.5">
            {to.map((persona) => (
              <span
                className="flex items-center gap-1.5 rounded-pill bg-where-soft py-0.5 pr-2.5 pl-0.5 text-caption font-semibold text-text-primary"
                key={persona.id}
              >
                <PersonaAvatar persona={persona} size="sm" />@
                {persona.name.split(' ')[0]}
              </span>
            ))}
          </p>
        ) : null}
        <p className="text-ui leading-relaxed font-semibold whitespace-pre-wrap text-text-primary">
          {to.length > 0
            ? said
            : segmentMentions(message.text, everyone).map((segment, i) =>
                segment.type === 'mention' ? (
                  <span
                    className="rounded-badge bg-where-soft px-1 text-where"
                    key={i}
                  >
                    {segment.text}
                  </span>
                ) : (
                  segment.text
                ),
              )}
        </p>
        {message.focus && onSpotlight ? (
          <div className="mt-2">
            <FocusChip label={message.focus.label} onClick={onSpotlight} />
          </div>
        ) : null}
      </div>
      <span
        aria-hidden="true"
        className="flex size-9 shrink-0 items-center justify-center rounded-full bg-where text-ui font-semibold text-white"
      >
        {initialsOf(asker).slice(0, 1)}
      </span>
    </div>
  );
}

/**
 * One persona's turn. While it is being spoken the text is the stream's draft;
 * once it lands it is the stored line. No sentiment label: a "reads
 * negative" tooltip on somebody's words second-guesses what they plainly
 * said.
 */
export function AnswerCard({
  draft,
  message,
  onAskDissenters,
  onChallenge,
  onOpenProfile,
  onSpotlight,
  onWhy,
  people,
  persona,
  sealed = false,
  seeProfile = false,
  spotlit = false,
  suggest,
  typing = false,
}: {
  draft?: string;
  message: ResearchRoomMessage;
  /** Asks the people who would push back why — from the reactions row. */
  onAskDissenters?: (ids: string[]) => void;
  /** "Does anyone disagree?" — the rest of the room, on this answer. */
  onChallenge?: () => void;
  onOpenProfile?: (id: string) => void;
  /** Takes the canvas to the part of the screen this answer is about. */
  onSpotlight?: () => void;
  /** "Why?" — asks the speaker to go one level deeper. */
  onWhy?: () => void;
  /** Everyone in the room, for the faces in the reactions row. */
  people?: Map<string, Persona>;
  persona?: Persona;
  /** An independent round still being answered: the words wait until
   *  everyone is done, so nobody's answer is read before the rest. */
  sealed?: boolean;
  /** "See full profile" in the top corner — on their hello, where the reader
   *  is first deciding who these people are. */
  seeProfile?: boolean;
  /** Whether that part is lit up on the canvas right now. */
  spotlit?: boolean;
  /**
   * Which follow-ups this answer earns (see `pickFollowUps`); only those
   * are offered. Absent: every follow-up given is offered.
   */
  suggest?: { challenge: boolean; why: boolean };
  /** Their answer is in and on its way to the screen — "typing", where a
   *  pending one is still "thinking". */
  typing?: boolean;
}) {
  const text = sealed
    ? ''
    : message.status === 'pending'
      ? (draft ?? '')
      : message.text;
  const done = message.status === 'done' && !sealed;
  // Which model wrote it, for the team comparing models — never customers.
  const isAdmin = useIsAdmin();

  // Only the follow-ups this answer earns: the rest are not offered at all,
  // not even on hover — a button under every answer is noise.
  const followUps = (['why', 'challenge'] as const).filter((mode) =>
    mode === 'why'
      ? Boolean(onWhy) && (suggest?.why ?? true)
      : Boolean(onChallenge) && (suggest?.challenge ?? true),
  );
  const hasReactions = Boolean(message.reactions?.length);

  return (
    <article className="group relative rounded-card border border-hairline bg-surface px-4 pt-4 pb-3 shadow-soft duration-500 animate-in fade-in slide-in-from-bottom-1 motion-reduce:animate-none">
      <div className="flex items-start gap-3">
        <button
          className="-m-1 flex min-w-0 cursor-pointer items-center gap-3 rounded-button p-1 text-left transition hover:bg-surface-hover disabled:cursor-default disabled:hover:bg-transparent"
          disabled={!persona || !onOpenProfile}
          onClick={() => {
            if (persona) onOpenProfile?.(persona.id);
          }}
          type="button"
        >
          {persona ? <PersonaAvatar persona={persona} size="lg" /> : null}
          <span className="min-w-0">
            <span className="flex items-center gap-1.5">
              <span className="truncate text-ui-lg leading-tight font-semibold text-text-primary">
                {message.speakerName}
              </span>
              {isAdmin && message.model ? (
                <span
                  className="shrink-0 rounded-pill bg-warm px-1.5 text-tag text-text-muted"
                  title={message.model}
                >
                  {personaChatModelLabel(message.model)}
                </span>
              ) : null}
            </span>
            {persona ? (
              <span className="mt-0.5 block truncate text-ui text-text-muted">
                {persona.title}
              </span>
            ) : null}
          </span>
        </button>
        {seeProfile && persona && onOpenProfile ? (
          <button
            className="ml-auto flex shrink-0 cursor-pointer items-center gap-1.5 rounded-button px-2 py-1 text-ui font-semibold text-text-primary transition hover:bg-surface-hover"
            onClick={() => {
              onOpenProfile(persona.id);
            }}
            type="button"
          >
            <SquareArrowOutUpRight aria-hidden="true" className="size-4" />
            See full profile
          </button>
        ) : message.focus && onSpotlight ? (
          <span className="ml-auto min-w-0 shrink pt-1">
            <FocusChip
              active={spotlit}
              label={message.focus.label}
              onClick={onSpotlight}
            />
          </span>
        ) : null}
      </div>

      <div className="mt-3">
        {message.status === 'failed' ? (
          <p className="text-ui text-text-muted italic">
            {message.speakerName} couldn&apos;t answer this time.
          </p>
        ) : text ? (
          <p
            className={`text-ui-lg leading-relaxed whitespace-pre-wrap text-text-primary ${persona ? accentOf(persona).selection : ''}`}
          >
            {text}
          </p>
        ) : sealed && message.status === 'done' ? (
          <p className="text-ui text-text-muted">
            Answered — waiting for the others…
          </p>
        ) : (
          <Working>
            {message.speakerName.split(' ')[0]} is{' '}
            {typing ? 'typing' : 'thinking'}…
          </Working>
        )}
      </div>

      {/* How the room took it on the left; following it up on the right —
          only where Jev says it is worth it. */}
      {done && (hasReactions || followUps.length > 0) ? (
        <div className="mt-3 flex items-center gap-2 border-t border-hairline pt-3">
          {hasReactions ? (
            <Reactions
              onAskDissenters={onAskDissenters}
              people={people}
              reactions={message.reactions ?? []}
            />
          ) : null}
          <span className="flex-1" />
          {followUps.map((mode) => (
            <FollowUpButton
              key={mode}
              label={
                mode === 'why'
                  ? `Ask ${message.speakerName.split(' ')[0]} to go one level deeper`
                  : 'Ask the others whether they see it differently'
              }
              mode={mode}
              onClick={mode === 'why' ? onWhy : onChallenge}
            />
          ))}
        </div>
      ) : null}
    </article>
  );
}

function FollowUpButton({
  label,
  mode,
  onClick,
}: {
  label: string;
  mode: 'challenge' | 'why';
  onClick?: () => void;
}) {
  const Icon = mode === 'why' ? CircleHelp : Swords;
  return (
    <Tooltip label={label}>
      <button
        className="flex h-8 cursor-pointer items-center gap-1.5 rounded-button px-2 text-ui font-semibold text-text-secondary transition hover:bg-surface-hover hover:text-text-primary active:scale-[0.97]"
        onClick={onClick}
        type="button"
      >
        <Icon aria-hidden="true" className="size-4" />
        {mode === 'why' ? 'Why?' : 'Challenge'}
      </button>
    </Tooltip>
  );
}

const STANCE = {
  agree: { Icon: ThumbsUp, label: 'would agree' },
  disagree: { Icon: ThumbsDown, label: 'would push back' },
  unsure: { Icon: CircleHelp, label: 'aren’t sure' },
} as const;

/**
 * How the rest of the room would take an answer, without anyone saying a
 * word: faces grouped by agree / push back / unsure. The push-back group is
 * a button — disagreement is the most useful thing in a session, and asking
 * about it should be one click.
 */
function Reactions({
  onAskDissenters,
  people,
  reactions,
}: {
  onAskDissenters?: (ids: string[]) => void;
  people?: Map<string, Persona>;
  reactions: NonNullable<ResearchRoomMessage['reactions']>;
}) {
  const groups = (['agree', 'disagree', 'unsure'] as const)
    .map((stance) => ({
      ids: reactions.filter((r) => r.stance === stance).map((r) => r.personaId),
      stance,
    }))
    .filter((group) => group.ids.length > 0);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {groups.map(({ ids, stance }) => {
        const { Icon, label } = STANCE[stance];
        const names = ids
          .map((id) => people?.get(id)?.name.split(' ')[0] ?? 'Someone')
          .join(', ');
        const content = (
          <>
            <Icon aria-hidden="true" className="size-4" />
            <span className="flex -space-x-2">
              {ids.map((id) => {
                const p = people?.get(id);
                return p ? (
                  <PersonaAvatar
                    className="ring-2 ring-surface"
                    key={id}
                    persona={p}
                    size="sm"
                  />
                ) : null;
              })}
            </span>
          </>
        );
        const title = `${names} ${label}`;
        return stance === 'disagree' && onAskDissenters ? (
          <Tooltip label={`${title} — ask why`} key={stance}>
            <button
              aria-label={`${title} — ask why`}
              className="flex h-9 cursor-pointer items-center gap-1.5 rounded-pill bg-tag-critical pr-1 pl-2.5 text-critical transition hover:brightness-95 active:scale-[0.97]"
              onClick={() => {
                onAskDissenters(ids);
              }}
              type="button"
            >
              {content}
            </button>
          </Tooltip>
        ) : (
          <Tooltip label={title} key={stance}>
            <span
              aria-label={title}
              className={`flex h-9 items-center gap-1.5 rounded-pill pr-1 pl-2.5 ${stance === 'agree' ? 'bg-tag-success text-success-ink' : 'bg-warm text-text-secondary'}`}
              role="img"
            >
              {content}
            </span>
          </Tooltip>
        );
      })}
    </div>
  );
}

function Chips({
  onPick,
  questions,
}: {
  onPick: (question: string) => void;
  questions: string[];
}) {
  if (questions.length === 0) return null;
  return (
    <div className="mt-2 flex flex-col gap-1.5">
      {questions.map((question) => (
        <button
          className="group flex cursor-pointer items-start gap-2 rounded-button bg-warm px-3 py-2 text-left text-caption leading-snug text-text-primary transition hover:bg-surface-hover active:scale-[0.99]"
          key={question}
          onClick={() => {
            onPick(question);
          }}
          type="button"
        >
          <CornerDownRight
            aria-hidden="true"
            className="mt-0.5 size-3.5 shrink-0 text-text-muted transition group-hover:text-brand"
          />
          {question}
        </button>
      ))}
    </div>
  );
}

/**
 * The poll a plain question reads as — its options, or its scale — with the
 * three ways on: run it, change it first, or ask the question as it was.
 */
function PollOffer({
  message,
  onAskAnyway,
  onEditPoll,
  onRunPoll,
}: {
  message: ResearchRoomMessage;
  onAskAnyway?: () => void;
  onEditPoll?: () => void;
  onRunPoll?: () => void;
}) {
  const poll = message.nudge?.poll;
  if (!poll) return null;
  const filled = poll.options.filter((option) => option.trim());
  const live = Boolean(onAskAnyway || onEditPoll || onRunPoll);

  return (
    <div className="mt-3">
      <div className="rounded-button bg-warm px-3 py-2.5">
        {poll.format === 'rating' ? (
          <p className="flex items-center justify-between gap-2 text-tag text-text-secondary">
            <span className="truncate">
              <span className="font-semibold text-text-primary">1</span>{' '}
              {poll.options[0]}
            </span>
            <span className="shrink-0 text-text-muted">…</span>
            <span className="truncate text-right">
              <span className="font-semibold text-text-primary">5</span>{' '}
              {poll.options[4]}
            </span>
          </p>
        ) : filled.length >= 2 ? (
          <ol className="space-y-1">
            {filled.map((option, i) => (
              <li
                className="flex gap-2 text-caption text-text-primary"
                key={option}
              >
                <span className="w-3 shrink-0 text-right text-text-muted tabular-nums">
                  {i + 1}
                </span>
                {option}
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-caption text-text-muted">
            The options aren&apos;t in the question — add them in the poll
            editor.
          </p>
        )}
      </div>
      {live ? (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          {onRunPoll ? (
            <button
              className="flex h-8 cursor-pointer items-center gap-1.5 rounded-button bg-brand px-3 text-caption font-semibold text-white transition hover:bg-brand-alt active:scale-[0.98]"
              onClick={onRunPoll}
              type="button"
            >
              <BarChart3 aria-hidden="true" className="size-3.5" />
              Run as a poll
            </button>
          ) : null}
          {onEditPoll ? (
            <button
              className="flex h-8 cursor-pointer items-center rounded-button px-3 text-caption font-semibold text-text-primary ring-1 ring-hairline-alt transition hover:bg-surface-hover"
              onClick={onEditPoll}
              type="button"
            >
              {onRunPoll ? 'Edit first' : 'Open the poll editor'}
            </button>
          ) : null}
          {onAskAnyway ? (
            <button
              className="h-8 cursor-pointer rounded-button px-2 text-caption font-medium text-text-secondary underline-offset-2 transition hover:text-text-primary hover:underline"
              onClick={onAskAnyway}
              type="button"
            >
              Ask as a normal question
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** The question Floto held back, quoted, so the researcher can see what
 *  "Ask it anyway" would send. */
function HeldQuestion({ message }: { message: ResearchRoomMessage }) {
  if (!message.nudge) return null;
  return (
    <p className="mb-2 border-l-2 border-hairline-alt pl-3 text-ui text-text-secondary italic">
      “{message.nudge.question}”
    </p>
  );
}

/** How a poll format reads in "Ask it anyway as …". */
const POLL_NOUN = {
  choice: 'a poll',
  ranking: 'a ranking',
  rating: 'a rating',
  'yes-no': 'a yes / no poll',
} as const;

const LABELS = {
  nudge: 'Before you ask',
  opener: 'Assistant',
  summary: 'What we heard',
  'wrap-up': 'Session wrap-up',
} as const;

/** Why Floto spoke up after a round, as its card says it. */
const MILESTONE_LABELS: Record<ResearchRoomMilestone, string> = {
  circling: 'Going in circles',
  contradiction: 'A contradiction',
  drifting: 'Drifting off topic',
  goal: 'Progress so far',
  insight: 'A clear takeaway',
  split: 'The room is split',
};

/**
 * Floto speaking outside a room's own history — setting the room up, before
 * there is a room to have said anything in. Drawn exactly like a `FlotoCard`,
 * so the notes that open a session read as the start of the same thread.
 */
/**
 * The room opening, in the thread: Floto saying the panel has joined, then
 * one short hello per person. Deliberately lighter than an answer — no card,
 * no reactions, no follow-ups — so meeting the panel reads as a moment, not
 * five more messages to get through before the room starts.
 */
export function IntroLine({
  message,
  onOpenProfile,
  persona,
  typing = false,
}: {
  message: ResearchRoomMessage;
  onOpenProfile?: (id: string) => void;
  persona?: Persona;
  /** Their hello is on its way. */
  typing?: boolean;
}) {
  // Floto's "I found N people…", in rooms opened before the setup card took
  // over saying so. New rooms don't post it.
  if (message.speakerKind === 'moderator') return null;
  return (
    // The same gap below the line as the thread leaves above it, so it sits
    // centred between the cards like a chat's divider, not hugging one.
    <div className="space-y-3">
      {/* Like a group chat's system line: somebody came in. */}
      <p className="text-center text-tag text-text-muted duration-300 animate-in fade-in motion-reduce:animate-none">
        {message.speakerName} joined the room
      </p>
      {/* The same card every answer is — a hello is them speaking — just
          without the reactions and follow-ups an answer has. */}
      <AnswerCard
        message={typing ? { ...message, status: 'pending', text: '' } : message}
        onOpenProfile={onOpenProfile}
        persona={persona}
        seeProfile
        typing={typing}
      />
    </div>
  );
}

export function ModeratorNote({
  children,
  label = 'Assistant',
  still = false,
  tone = 'default',
}: {
  children: ReactNode;
  label?: string;
  /** Already on screen before this render — a note carried over from the
   *  screen before must not arrive a second time. */
  still?: boolean;
  tone?: 'critical' | 'default';
}) {
  return (
    <article
      className={[
        'rounded-card bg-surface px-4 py-3.5 ring-1',
        still
          ? ''
          : 'duration-300 animate-in fade-in slide-in-from-bottom-1 motion-reduce:animate-none',
        tone === 'critical'
          ? 'border-l-2 border-l-critical ring-hairline'
          : 'ring-hairline',
      ].join(' ')}
    >
      <header className="flex items-center gap-2">
        <FlotoMark />
        <p className="flex-1 text-ui">
          <span className="font-semibold text-text-primary">Floto</span>
          <span className="text-text-muted"> · {label}</span>
        </p>
      </header>
      <div className="mt-2">{children}</div>
    </article>
  );
}

/**
 * Floto's cards — the welcome, each round's read, and the wrap-up. The two
 * reads are addressed to the researcher and never heard by the personas,
 * which is what "only you" means; the welcome says nothing private, so it
 * does not carry the mark.
 */
export function FlotoCard({
  fixList,
  footer,
  message,
  onAskAnyway,
  onEditPoll,
  onPick,
  onRunPoll,
}: {
  /** A round's read whose round changed the fix list: how many are left to
   *  fix, and the way there. Absent when the round did not change it. */
  fixList?: { onOpen: () => void; open: number };
  /** A line under a hairline at the card's foot — a round's read carries
   *  what the round cost here. */
  footer?: ReactNode;
  message: ResearchRoomMessage;
  /** For a nudge still waiting on the researcher: send the held question
   *  as it was. Absent once they have moved on. */
  onAskAnyway?: () => void;
  /** "Reads like a poll": open the offered poll in the composer's editor. */
  onEditPoll?: () => void;
  /** Puts a suggested question in the composer. Absent in an ended
   *  session, where there is nothing left to ask. */
  onPick?: (question: string) => void;
  /** "Reads like a poll": run the offered poll as shown. Absent when its
   *  options could not be read and need filling in first. */
  onRunPoll?: () => void;
}) {
  const kind = message.kind as keyof typeof LABELS;
  const isPrivate = kind !== 'opener';
  const questions = message.suggestedQuestions ?? [];
  // A round's read shows only when Floto had something to point out.
  const label =
    kind === 'summary' && message.milestone
      ? MILESTONE_LABELS[message.milestone]
      : LABELS[kind];

  return (
    <article className="rounded-card border border-hairline bg-surface px-4 pt-4 pb-3.5 shadow-soft duration-200 animate-in fade-in slide-in-from-bottom-1 motion-reduce:animate-none">
      <header className="flex items-center gap-2.5">
        {/* Floto's mark, white on its red — the moderator's face, the way a
            persona's portrait is theirs. */}
        <span className="flex size-8 shrink-0 items-center justify-center rounded-button bg-critical">
          <img
            alt=""
            aria-hidden="true"
            className="size-4 brightness-0 invert"
            src="/icons/icon-32.png"
            srcSet="/icons/icon-32.png 1x, /icons/icon-128.png 4x"
          />
        </span>
        <p className="flex-1 text-ui-lg">
          <span className="font-semibold text-text-primary">Floto</span>
          <span className="ml-2 text-text-muted">{label}</span>
        </p>
        {isPrivate ? (
          <Tooltip label="The personas don't hear this">
            <span className="flex items-center gap-1.5 text-caption font-semibold text-text-muted">
              <Eye aria-hidden="true" className="size-4" />
              Only you
            </span>
          </Tooltip>
        ) : null}
      </header>

      <div className="mt-3">
        {message.status === 'pending' ? (
          kind === 'nudge' ? (
            <>
              <HeldQuestion message={message} />
              <p className="text-ui leading-relaxed text-text-primary">
                {message.text}
              </p>
              <div className="mt-2">
                <Working>Writing a sharper version…</Working>
              </div>
            </>
          ) : (
            <Working>
              {kind === 'wrap-up'
                ? 'Writing up the session…'
                : 'Reading the round…'}
            </Working>
          )
        ) : message.status === 'failed' ? (
          <p className="text-ui text-text-muted italic">
            Floto couldn&apos;t read this one.
          </p>
        ) : (
          <>
            {kind === 'nudge' ? <HeldQuestion message={message} /> : null}
            <p className="text-ui-lg leading-relaxed whitespace-pre-wrap text-text-secondary">
              {message.text}
            </p>
            {message.findings && message.findings.length > 0 ? (
              <ol className="mt-3 space-y-2">
                {message.findings.map((finding, i) => (
                  <li className="flex gap-2.5" key={finding}>
                    <span className="flex size-5 shrink-0 items-center justify-center rounded-pill bg-warm text-tag font-semibold text-text-secondary tabular-nums">
                      {i + 1}
                    </span>
                    <span className="text-caption leading-relaxed text-text-secondary">
                      {finding}
                    </span>
                  </li>
                ))}
              </ol>
            ) : null}
            {/* The opener introduces its own suggestions. */}
            {onPick &&
            questions.length > 0 &&
            kind !== 'nudge' &&
            kind !== 'opener' ? (
              <p className="mt-4 text-small-label text-text-muted uppercase">
                {kind === 'wrap-up' ? 'Still open' : 'Ask next'}
              </p>
            ) : null}
            {onPick ? <Chips onPick={onPick} questions={questions} /> : null}
            {kind === 'nudge' && message.nudge?.reason === 'as-poll' ? (
              <PollOffer
                message={message}
                onAskAnyway={onAskAnyway}
                onEditPoll={onEditPoll}
                onRunPoll={onRunPoll}
              />
            ) : kind === 'nudge' && onAskAnyway ? (
              <button
                className="mt-2 cursor-pointer rounded-button px-1 py-1 text-caption font-semibold text-text-secondary underline-offset-2 transition hover:text-text-primary hover:underline"
                onClick={onAskAnyway}
                type="button"
              >
                {message.nudge?.poll
                  ? `Ask it anyway as ${POLL_NOUN[message.nudge.poll.format]}`
                  : 'Ask it anyway'}
              </button>
            ) : null}
          </>
        )}
      </div>
      {fixList && message.status === 'done' ? (
        <button
          className="mt-4 flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-card border border-hairline-alt bg-surface text-ui-strong font-semibold text-text-primary transition duration-300 animate-in fade-in hover:bg-surface-hover active:scale-[0.99] motion-reduce:animate-none"
          onClick={fixList.onOpen}
          type="button"
        >
          <ListChecks aria-hidden="true" className="size-4" />
          {fixList.open > 0
            ? `Open fix list · ${String(fixList.open)} to fix`
            : 'Open fix list'}
        </button>
      ) : null}
      {footer ? (
        <footer className="mt-3 border-t border-hairline pt-2.5">
          {footer}
        </footer>
      ) : null}
    </article>
  );
}
